import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { useUserData } from '../hooks/useUserData';
import { useScenarios } from '../hooks/useScenarios';

const Dashboard = ({ supabase }) => {
  const { userData, loading: userLoading } = useUserData(supabase);
  const { scenarios, loading: scenariosLoading } = useScenarios(supabase);

  const [patStatus, setPatStatus] = useState(null);
  const [showPatModal, setShowPatModal] = useState(false);
  const [newPat, setNewPat] = useState('');
  const [patUpdateLoading, setPatUpdateLoading] = useState(false);
  const [patUpdateError, setPatUpdateError] = useState(null);
  
  const [showLogoModal, setShowLogoModal] = useState(false);
  const [newLogoUrl, setNewLogoUrl] = useState('');
  const [logoUpdateLoading, setLogoUpdateLoading] = useState(false);
  const [logoUpdateError, setLogoUpdateError] = useState(null);

  useEffect(() => {
    const checkPatStatus = async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error || !session?.access_token) return;
      const { data: patStatus } = await supabase.functions.invoke('pat-status', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      setPatStatus(patStatus);
    };

    if (userData?.id) checkPatStatus();
  }, [userData?.id, supabase]);

  const handlePatUpdate = async () => {
    try {
      setPatUpdateLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke('pat-update', {
        body: { new_pat: newPat },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error || !data.success) throw new Error(data?.error || 'Failed to update PAT');
      setShowPatModal(false);
      setNewPat('');

      const { data: updatedStatus } = await supabase.functions.invoke('pat-status', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      setPatStatus(updatedStatus);
    } catch (err) {
      setPatUpdateError(err.message);
    } finally {
      setPatUpdateLoading(false);
    }
  };

  const handleEditScenario = (scenario) => {
    console.log('Edit scenario:', scenario);
  };

  const handleLogoUpdate = async () => {
    try {
      setLogoUpdateLoading(true);
      setLogoUpdateError(null);
      
      const { data: { session } } = await supabase.auth.getSession();
      const { error } = await supabase
        .from('users')
        .update({ logo_url: newLogoUrl })
        .eq('id', session.user.id);

      if (error) throw error;
      
      setShowLogoModal(false);
      setNewLogoUrl('');
      
      // Refresh user data to show updated logo
      window.location.reload();
    } catch (err) {
      setLogoUpdateError(err.message);
    } finally {
      setLogoUpdateLoading(false);
    }
  };

  if (userLoading || !userData) {
    return <div className="text-center py-10 text-gray-500">Loading account info...</div>;
  }

  const subscription = userData.subscription;
  const isUnlimitedPlan = !subscription?.plan?.max_scans || subscription.plan.max_scans === 0;
  const scanProgressPercentage = isUnlimitedPlan
    ? 100
    : Math.min(
        100,
        ((subscription.total_operations - subscription.operations_remaining) / subscription.total_operations) * 100
      );

  return (
    <div className="bg-gray-50">
      <main className="px-0">
        {/* Overview - Removed top margin */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Account Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card title="Current Plan" value={subscription?.plan?.name || 'Free'}>
              <button className="mt-4 text-sm text-indigo-600 font-medium hover:text-indigo-800">
                Upgrade Plan
              </button>
            </Card>

            <Card title="Scans Remaining" value={isUnlimitedPlan ? 'Unlimited' : subscription.operations_remaining}>
              {!isUnlimitedPlan && (
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-indigo-600 h-2 rounded-full"
                    style={{ width: `${scanProgressPercentage}%` }}
                  />
                </div>
              )}
            </Card>

            <Card title="Total Scans Used" value={userData.total_scans || 0} />

            <Card title="Airtable Connection">
              <div className="flex items-center mt-2">
                <span
                  className={`h-3 w-3 rounded-full mr-2 ${
                    patStatus?.valid ? 'bg-green-500' : 'bg-red-500'
                  }`}
                />
                <p className="font-medium">{patStatus?.valid ? 'Connected' : 'Disconnected'}</p>
              </div>
              <button
                onClick={() => setShowPatModal(true)}
                className="mt-4 text-sm text-indigo-600 font-medium hover:text-indigo-800"
              >
                {patStatus?.valid ? 'Update API Key' : 'Add API Key'}
              </button>
            </Card>
          </div>
        </div>

        {/* Logo Configuration */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Branding</h2>
          <Card title="Company Logo">
            {userData.logo_url ? (
              <div className="mt-2">
                <img 
                  src={userData.logo_url} 
                  alt="Company Logo" 
                  className="h-16 w-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <p className="text-sm text-red-500 mt-1" style={{display: 'none'}}>
                  Failed to load logo
                </p>
              </div>
            ) : (
              <p className="mt-2 text-gray-500">No logo configured</p>
            )}
            <button
              onClick={() => {
                setNewLogoUrl(userData.logo_url || '');
                setShowLogoModal(true);
              }}
              className="mt-4 text-sm text-indigo-600 font-medium hover:text-indigo-800"
            >
              {userData.logo_url ? 'Update Logo' : 'Add Logo'}
            </button>
          </Card>
        </div>

        {/* Scenarios */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Your Scenarios</h2>
            <button className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center hover:bg-indigo-700">
              <Plus className="h-4 w-4 mr-1" />
              Create Scenario
            </button>
          </div>

          {scenariosLoading ? (
            <div className="text-center text-gray-500">Loading scenarios...</div>
          ) : scenarios.length === 0 ? (
            <div className="text-center text-gray-500">
              <p>No scenarios yet.</p>
              <p className="text-sm mt-1">Create one to get started.</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {scenarios.map((scenario) => (
                  <li key={scenario.id}>
                    <div className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">{scenario.name}</h3>
                        <div className="ml-2 flex-shrink-0 flex">
                          <span className="text-sm text-gray-500">{scenario.total_scans || 0} scans</span>
                          {scenario.last_scan_at && (
                            <span className="ml-3 text-sm text-gray-500">
                              Last used: {new Date(scenario.last_scan_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      {scenario.description && (
                        <p className="mt-2 text-sm text-gray-600">{scenario.description}</p>
                      )}
                      <div className="mt-3 flex justify-end">
                        <button
                          onClick={() => handleEditScenario(scenario)}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* PAT Update Modal */}
        {showPatModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white p-6 rounded-lg max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Update Airtable PAT</h3>
              <input
                type="password"
                value={newPat}
                onChange={(e) => setNewPat(e.target.value)}
                className="w-full px-3 py-2 border rounded-md mb-3"
                placeholder="pat_xxxxx"
                disabled={patUpdateLoading}
              />
              {patUpdateError && <div className="text-red-600 mb-3">{patUpdateError}</div>}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowPatModal(false)}
                  className="text-gray-600 hover:text-gray-800"
                  disabled={patUpdateLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handlePatUpdate}
                  disabled={patUpdateLoading || !newPat}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center"
                >
                  {patUpdateLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Updating...
                    </>
                  ) : (
                    'Update'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Logo Update Modal */}
        {showLogoModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white p-6 rounded-lg max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Update Logo URL</h3>
              <input
                type="url"
                value={newLogoUrl}
                onChange={(e) => setNewLogoUrl(e.target.value)}
                className="w-full px-3 py-2 border rounded-md mb-3"
                placeholder="https://example.com/logo.png"
                disabled={logoUpdateLoading}
              />
              <p className="text-sm text-gray-500 mb-3">
                Enter a URL to an image file (PNG, JPG, SVG, etc.)
              </p>
              {logoUpdateError && <div className="text-red-600 mb-3">{logoUpdateError}</div>}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowLogoModal(false)}
                  className="text-gray-600 hover:text-gray-800"
                  disabled={logoUpdateLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleLogoUpdate}
                  disabled={logoUpdateLoading}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center"
                >
                  {logoUpdateLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Updating...
                    </>
                  ) : (
                    'Update'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const Card = ({ title, value, children }) => (
  <div className="bg-white p-6 rounded-lg shadow">
    <p className="text-sm text-gray-500">{title}</p>
    {value !== undefined && <p className="text-2xl font-bold">{value}</p>}
    {children}
  </div>
);

export default Dashboard;